// src/data/reviewsClient.js
//
// P-19 S1: Reviews surface — API client.
//
// Three exports:
//   getReviews()                — GET /api/reviews — monthly agenda + status
//   checkReviewItem(itemRef)    — POST /api/reviews/:itemRef/check
//   uncheckReviewItem(itemRef)  — DELETE /api/reviews/:itemRef/check

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

// ─── Internal fetch helper ────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...options,
    });
  } catch (err) {
    log.error("reviewsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }

  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("reviewsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }

  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── GET /api/reviews ─────────────────────────────────────────────────────────

/**
 * Fetch the Portfolio Command monthly agenda for the current cycle.
 * Returns { cycle, config, items, summary: { total, reviewed } }.
 * items: Array<{ itemRef, label, bucket, cadence, status, reviewedAt, reviewedBy, meta }>
 */
export async function getReviews() {
  return apiFetch("/api/reviews");
}

const weeklyUrl = "/api/portfolio/review-meetings";
export const getReviewMeetings = () => apiFetch(weeklyUrl);
export const markReviewMeetingHeld = (id, data) => apiFetch(`${weeklyUrl}/${encodeURIComponent(id)}/held`, {
  method: "POST", body: JSON.stringify(data),
});
export const editReviewMeetingLog = (id, logId, data) => apiFetch(
  `${weeklyUrl}/${encodeURIComponent(id)}/logs/${encodeURIComponent(logId)}`,
  { method: "PATCH", body: JSON.stringify(data) },
);
export const deleteReviewMeetingLog = (id, logId) => apiFetch(
  `${weeklyUrl}/${encodeURIComponent(id)}/logs/${encodeURIComponent(logId)}`, { method: "DELETE" },
);

// ─── POST /api/reviews/:itemRef/check ─────────────────────────────────────────

/**
 * Mark a review item as checked for the current cycle.
 * Writes a portfolio_reviews row + audit_log entry.
 *
 * @param {string} itemRef — stable item identifier, e.g. "lease_action:LSE.123"
 * @returns {Promise<{ id, itemRef, itemLabel, bucket, reviewedAt, cycle }>}
 */
export async function checkReviewItem(itemRef) {
  return apiFetch(`/api/reviews/${encodeURIComponent(itemRef)}/check`, {
    method: "POST",
    body:   "{}",
  });
}

// ─── DELETE /api/reviews/:itemRef/check ───────────────────────────────────────

/**
 * Undo a review item for the current monthly cycle.
 * Soft-deletes the review row and writes an audit_log entry.
 *
 * @param {string} itemRef — stable item identifier
 * @returns {Promise<{ ok, itemRef, cycle }>}
 */
export async function uncheckReviewItem(itemRef) {
  return apiFetch(`/api/reviews/${encodeURIComponent(itemRef)}/check`, {
    method: "DELETE",
  });
}
